import React, { useRef, useEffect } from 'react';
import { Formik, Form, Field, CccisdInput } from 'cccisd-formik';
import Modal from 'cccisd-modal';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import IconAdd from 'cccisd-icons/plus2';
import Tooltip from 'cccisd-tooltip';

const Boilerplate = window.cccisd.boilerplate;

export default function AddRespondent({ deployment, disabled = false, onSubmitSuccess }) {
    const addResModal = useRef(null);

    useEffect(() => {
        if (disabled && addResModal.current) {
            addResModal.current.close();
        }
    }, [disabled]);

    const closeModal = () => {
        if (addResModal.current) {
            addResModal.current.close();
        }
    };

    const handleCancel = () => {
        closeModal();
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            if (!deployment?.deploymentId) {
                notification({
                    type: 'danger',
                    message: 'Please select a site before adding a respondent.',
                });
                return;
            }

            // groupId can come from a deployment assignment or an explicit prop from parent.
            const groupId = deployment?.assignment?.groupId || deployment?.groupId;

            if (!groupId) {
                notification({
                    type: 'danger',
                    message: 'Unable to resolve the selected site. Please re-select a site.',
                });
                return;
            }

            const payload = {
                ...values,
                activate: false,
                role: 'respondent',
                first_name: values.firstName,
                last_name: values.lastName,
                group: groupId,
            };

            const response = await axios.post(Boilerplate.route('api.nexus.pawn.store'), payload);
            if (response.data.errors) {
                if (response.data.errors.username) {
                    notification({
                        type: 'danger',
                        message: response.data.errors.username[0],
                    });
                } else {
                    notification({
                        type: 'danger',
                        message: 'Unable to add respondent. Please try again.',
                    });
                }
                return;
            }

            const pawnId = response?.data?.data?.id || null;

            const respondentResponse = await axios.post(
                Boilerplate.route('api.assignmentDeploymentRespondent.store', {
                    deploymentId: deployment.deploymentId,
                }),
                {
                    pawnIds: [pawnId],
                }
            );

            if (respondentResponse.data.errors) {
                notification({
                    type: 'danger',
                    message: 'Unable to add respondent. Please try again.',
                });
                return;
            }

            // @todo if respondentResponse has errors, delete created pawn
            closeModal();

            if (typeof onSubmitSuccess === 'function') {
                await onSubmitSuccess();
            }
        } catch (error) {
            notification({
                type: 'danger',
                message: 'Unable to add respondent. Please try again.',
            });
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    const validate = values => {
        const errors = {};

        return errors;
    };

    if (disabled) {
        return (
            <Tooltip title={disabled === true ? 'Select a site first' : ''}>
                <button type="button" className="btn btn-primary" disabled>
                    <IconAdd spaceright /> Add Respondent
                </button>
            </Tooltip>
        );
    }

    return (
        <Modal
            ref={addResModal}
            title="Add Respondent"
            trigger={
                <button type="button" className="btn btn-primary">
                    <IconAdd spaceright /> Add Respondent
                </button>
            }
        >
            <Formik
                initialValues={{
                    username: '',
                    firstName: '',
                    lastName: '',
                }}
                validate={validate}
                onSubmit={handleSubmit}
                render={({ isSubmitting }) => (
                    <Form>
                        <div className="form-group">
                            <label htmlFor="firstName">First Name (optional)</label>
                            <Field id="firstName" name="firstName" type="text" component={CccisdInput} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="lastName">Last Name (optional)</label>
                            <Field id="lastName" name="lastName" type="text" component={CccisdInput} />
                        </div>

                        <div className="form-group">
                            <label htmlFor="username">Username (optional)</label>
                            <Field id="username" name="username" type="text" component={CccisdInput} />
                        </div>

                        <div className="d-flex justify-content-between" style={{ marginTop: '15px' }}>
                            <button type="button" onClick={handleCancel} className="btn btn-secondary">
                                Cancel
                            </button>
                            <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                                Add Respondent
                            </button>
                        </div>
                    </Form>
                )}
            />
        </Modal>
    );
}
